// Foraging channel client. One channel per page mount; the page
// connects on `useEffect` and tears down on unmount.

import { Channel } from "phoenix";
import { getSocket } from "./rooms";

export type ForageKind = "apple_trees" | "strawberry_bushes";

export interface ForagePlant {
  id: string;
  x_frac: number;
  y_frac: number;
  size: number;
  rotation: number;
  has_leaf: boolean;
  z: number;
  state: "green" | "red" | "falling";
  red_at: number | null;
  falls_at: number | null;
  // Ripened during a Frenzy — skip the green→red fade and render fully
  // red immediately.
  instant: boolean;
}

export interface ForageSkill {
  skill_id: string;
  xp: number;
  level: number;
  xp_into_level: number;
  xp_to_next: number;
}

export type FrenzyPhase = "charging" | "ready" | "active";

export interface FrenzyStatus {
  phase: FrenzyPhase;
  // ms left in the current phase (0 while `ready`, which waits on a
  // click). The bar animation runs locally off these two numbers.
  remaining_ms: number;
  duration_ms: number;
}

export interface ForageState {
  kind: ForageKind;
  level: number;
  slots: ForagePlant[];
  skill: ForageSkill;
  frenzy: FrenzyStatus;
}

export interface HarvestSuccess {
  slot: ForagePlant;
  skill: ForageSkill;
  gained: number;
  leveled_up: boolean;
}

export type EvictionReason =
  | "acquired_by_sleep"
  | "acquired_by_battle"
  | "acquired_by_other_tab"
  | "idle"
  | "test_cleanup";

interface JoinHandlers {
  onState: (state: ForageState) => void;
  onSlotsChanged: (slots: ForagePlant[]) => void;
  onFrenzy: (status: FrenzyStatus) => void;
  onEvicted: (reason: EvictionReason) => void;
  onError: (msg: string) => void;
}

function getTabId(): string {
  // sessionStorage is per-tab; survives reloads of *this* tab but
  // is not shared with other tabs / windows. That's exactly what we
  // need to distinguish "same-tab reconnect" from "second tab".
  if (typeof window === "undefined") return "server";
  try {
    const KEY = "doi_tab_id";
    let v = window.sessionStorage.getItem(KEY);
    if (!v) {
      v =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `t_${Math.random().toString(36).slice(2)}_${Date.now()}`;
      window.sessionStorage.setItem(KEY, v);
    }
    return v;
  } catch {
    return `t_${Math.random().toString(36).slice(2)}`;
  }
}

export async function joinForage(
  userId: number,
  kind: ForageKind,
  handlers: JoinHandlers,
): Promise<{
  channel: Channel;
  harvest: (slotId: string) => Promise<HarvestResult>;
  frenzy: () => Promise<FrenzyResult>;
}> {
  const socket = await getSocket();
  const channel = socket.channel(`forage:${userId}:${kind}`, {
    tab_id: getTabId(),
  });

  channel.on("state", (payload: ForageState) => handlers.onState(payload));
  channel.on("slots_changed", (payload: { slots: ForagePlant[] }) =>
    handlers.onSlotsChanged(payload.slots),
  );
  channel.on("frenzy", (payload: FrenzyStatus) => handlers.onFrenzy(payload));
  channel.on("evicted", (payload: { reason: EvictionReason }) =>
    handlers.onEvicted(payload.reason),
  );

  await new Promise<void>((resolve, reject) => {
    channel
      .join()
      .receive("ok", () => resolve())
      .receive("error", (resp: { reason?: string }) => {
        handlers.onError(resp?.reason ?? "join_failed");
        reject(new Error(resp?.reason ?? "join_failed"));
      })
      .receive("timeout", () => {
        handlers.onError("timeout");
        reject(new Error("timeout"));
      });
  });

  const harvest = (slotId: string): Promise<HarvestResult> =>
    new Promise((resolve) => {
      channel
        .push("harvest", { slot_id: slotId })
        .receive("ok", (payload: HarvestSuccess) =>
          resolve({ ok: true, payload }),
        )
        .receive("error", (payload: { reason: HarvestErrorReason }) =>
          resolve({ ok: false, reason: payload.reason }),
        )
        .receive("timeout", () =>
          resolve({ ok: false, reason: "timeout" }),
        );
    });

  const frenzy = (): Promise<FrenzyResult> =>
    new Promise((resolve) => {
      channel
        .push("frenzy", {})
        .receive("ok", (payload: FrenzyStatus) =>
          resolve({ ok: true, payload }),
        )
        .receive("error", (payload: { reason: string }) =>
          resolve({ ok: false, reason: payload.reason }),
        )
        .receive("timeout", () => resolve({ ok: false, reason: "timeout" }));
    });

  return { channel, harvest, frenzy };
}

export type HarvestErrorReason =
  | "not_ripe"
  | "gone"
  | "inventory_full"
  | "bad_payload"
  | "timeout";

export type HarvestResult =
  | { ok: true; payload: HarvestSuccess }
  | { ok: false; reason: HarvestErrorReason };

export type FrenzyResult =
  | { ok: true; payload: FrenzyStatus }
  | { ok: false; reason: string };
